import { useTags } from "@/features/modals/TagsModal/tags";
import { useModals } from "@/features/modals/hooks";
import { useOrderCreationMethods } from "@/features/projects/filter/orderMethod";
import { useAiFeatureConfig } from "@/features/projects/hooks/useAiFeatureConfig";
import styled from "@emotion/styled";
import { type Widget, type WidgetSizeDefinition } from "@repo/api";
import {
  Button,
  Text,
  Variables,
  arrowLeftIcon,
  documentIcon,
} from "@repo/skapa";
import { T, t } from "@repo/translation";
import {
  ButtonsContainer,
  ConfirmModal,
  FlexDiv,
  Line,
  PaddedContainer,
  SectionContainer,
  translateLabel,
  useBokaServices,
  useSettings,
  useToast,
} from "@repo/ui";
import { hasItems } from "@repo/utils";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { TemplateSelectionProvider } from "../templates/TemplateSelection";
import { WIDGETS } from "../widgets/widgetSettings/widgetsSettings";
import { CustomizePanel } from "./CustomizePanel";
import { InstalledWidgets } from "./InstalledWidgets";
import { WidgetBox } from "./WidgetBox";
import { groupWidgets, useAvailableWidgets } from "./useAvailableWidgets";
import { useInstalledWidgets } from "./useInstalledWidgets";

const PageRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${Variables.space150};
  min-width: 0;
`;

const PageColumn = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

export const CustomizeWorkspacePage = () => {
  const { updateToast } = useToast();
  const { pushModal } = useModals();
  const {
    quotation,
    feature_flags,
    customer_communication,
    kic_config,
    consent_requirements,
    general,
    project_stages,
    waiting_list,
  } = useSettings();
  const isWorkspaceTemplatesEnabled =
    feature_flags?.enable_workspace_templates || false;
  const navigate = useNavigate();
  const { data: tags } = useTags();
  const bokaServicesList = useBokaServices();
  const orderCreationMethods = useOrderCreationMethods();

  const isGeneratedProjectsEnabled = useAiFeatureConfig({
    feature: "generatedProjects",
  });

  const [selectedWidgetId, setSelectedWidgetId] = useState<number | undefined>(
    undefined
  );
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showTemplatesPanel, setShowTemplatesPanel] = useState(false);

  const widgetSettings = WIDGETS({
    hasTags: hasItems(tags),
    supportedLanguages: general?.supported_languages,
    bokaServices: bokaServicesList,
    orderMethods: orderCreationMethods,
    isColdProjectEnabled:
      customer_communication?.cold_projects?.enable || false,
    isGeneratedProjectsEnabled,
    isKICEnabled: kic_config?.enabled || false,
    isPromotionalConsentEnabled:
      consent_requirements?.promotional_consent || false,
    isSalesForceEnabled: feature_flags?.enable_salesforce_integration || false,
    isProjectStagesEnabled: project_stages?.enable || false,
    projectStageValues: project_stages?.stages?.map((s) => s.stage_key) ?? [],
    isWaitingListEnabled: waiting_list?.waiting_list_enabled || false,
  });

  const availableWidgets = useAvailableWidgets(
    widgetSettings,
    quotation?.enable_quotations_import || false,
    feature_flags?.enable_salesforce_integration || false
  );

  const {
    installedWidgets,
    addWidget,
    updateWidget,
    removeWidget,
    reorderWidget,
    moveWidgetToPosition,
    updateWidgetSize,
    resetWorkspace,
  } = useInstalledWidgets(updateToast);
  const groupedWidgets = groupWidgets(availableWidgets);

  const selectedWidget = useMemo(() => {
    const widgetData = installedWidgets.find(
      (widget) => widget.workspacePreferenceId === selectedWidgetId
    );
    const widget = availableWidgets.find(
      (w) => w.widgetType === widgetData?.widgetType
    );

    if (!widget || !widgetData) {
      return undefined;
    }

    return {
      ...widget,
      ...widgetData,
    };
  }, [selectedWidgetId, installedWidgets, availableWidgets]);

  const selectedWidgetLabel =
    translateLabel(selectedWidget?.widgetAliasName ?? selectedWidget?.name) ||
    t("action.adjust_settings");

  const closePanel = useCallback(() => {
    setSelectedWidgetId(undefined);
    setShowTemplatesPanel(false);
  }, []);

  const selectWidget = useCallback((id: number | undefined) => {
    setShowTemplatesPanel(false);
    setSelectedWidgetId(id);
  }, []);

  const openTemplatesPanel = useCallback(() => {
    setSelectedWidgetId(undefined);
    setShowTemplatesPanel(true);
  }, []);

  const isPanelOpen = showTemplatesPanel || selectedWidget !== undefined;
  const panelLabel = showTemplatesPanel
    ? t("label.workspace_templates")
    : selectedWidgetLabel;

  const handleRenameWidget = useCallback(() => {
    if (!selectedWidget) {
      return;
    }
    const currentName = selectedWidget.widgetAliasName ?? selectedWidget.name;
    pushModal({
      type: "INPUT_VALUE",
      initialValue: translateLabel(currentName),
      onSave: (val?: string) => {
        if (val) {
          updateWidget({
            ...selectedWidget,
            widgetAliasName: val,
          });
        }
      },
      title: t("label.change_widget_name"),
    });
  }, [selectedWidget, pushModal, updateWidget]);

  const panel = (
    <CustomizePanel
      isOpen={isPanelOpen}
      showTemplates={showTemplatesPanel}
      panelLabel={panelLabel}
      selectedWidget={selectedWidget}
      selectedWidgetLabel={selectedWidgetLabel}
      onClose={closePanel}
      onRenameWidget={handleRenameWidget}
      onUpdateWidget={updateWidget}
      onUpdateWidgetSize={updateWidgetSize}
    />
  );

  return (
    <PaddedContainer>
      <PageRow>
        <PageColumn>
          <SectionContainer>
            <FlexDiv justifyContent="space-between" alignItems="center">
              <ButtonsContainer>
                <Button
                  iconOnly
                  ssrIcon={arrowLeftIcon}
                  size="small"
                  type="secondary"
                  onClick={() => {
                    navigate("/workspace/");
                  }}
                />
                <Text textStyle="Heading.M">
                  <T translationKey="action.adjust_settings" />
                </Text>
              </ButtonsContainer>
              {isWorkspaceTemplatesEnabled && (
                <Button
                  type="secondary"
                  size="small"
                  ssrIcon={documentIcon}
                  text={t("label.workspace_templates")}
                  onClick={openTemplatesPanel}
                />
              )}
            </FlexDiv>
            <Text>
              <T translationKey="prompt.customize_workspace_desc" />
            </Text>
            <SectionContainer>
              <InstalledWidgets
                installedWidgets={installedWidgets}
                allWidgets={availableWidgets}
                selectedWidget={selectedWidget}
                setSelectedWidgetId={selectWidget}
                onReorderWidget={reorderWidget}
                onMoveWidgetToPosition={moveWidgetToPosition}
                onRemoveWidget={(widgetId: number) => {
                  return removeWidget(widgetId).then(() => {
                    setSelectedWidgetId(undefined);
                  });
                }}
              />
              <Line />
              <Text textStyle="Heading.S">
                <T translationKey="label.widget_list" />
              </Text>
              {Object.entries(groupedWidgets).map(([category, widgets]) => {
                return (
                  <div key={category}>
                    <FlexDiv gap={Variables.space100} wrap>
                      {widgets.map((widget) => {
                        return (
                          <WidgetBox
                            key={widget.widgetType}
                            widget={widget}
                            onAddWidget={async (
                              w: Widget,
                              appSize: WidgetSizeDefinition,
                              appSettings: Record<string, object>
                            ) => {
                              const newInstalledWidget = await addWidget(
                                w,
                                appSize,
                                appSettings
                              );
                              if (newInstalledWidget) {
                                selectWidget(
                                  newInstalledWidget.workspacePreferenceId
                                );
                              }
                            }}
                          />
                        );
                      })}
                    </FlexDiv>
                  </div>
                );
              })}
            </SectionContainer>
          </SectionContainer>
        </PageColumn>
        {isWorkspaceTemplatesEnabled ? (
          <TemplateSelectionProvider
            availableWidgets={availableWidgets}
            onResetWorkspace={
              installedWidgets.length > 0
                ? () => setShowResetConfirm(true)
                : undefined
            }
          >
            {panel}
          </TemplateSelectionProvider>
        ) : (
          panel
        )}
      </PageRow>
      <ConfirmModal
        visible={showResetConfirm}
        title={t("prompt.confirm_reset_workspace_title")}
        message={t("prompt.confirm_reset_workspace")}
        onConfirm={() => {
          setShowResetConfirm(false);
          resetWorkspace().then(() => {
            setSelectedWidgetId(undefined);
          });
        }}
        onCancel={() => setShowResetConfirm(false)}
        onClose={() => setShowResetConfirm(false)}
      />
    </PaddedContainer>
  );
};
